import { Typography } from '@bowtie-ins/bowkit';
import { CheckboxOn } from '@bowtie-ins/bowkit/icons';
import {
  BLUSH,
  BOWTIE_BLUE,
  BOWTIE_PINK,
  GRAPHITE,
  INK,
  STONE,
  WHITE,
} from '@bowtie-ins/bowkit/palette';
import { Product, Quotation } from '@bowtie-web/common';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import { Theme, makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { startCase } from 'lodash';
import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useNumberCountUp from '../../hooks/useNumberCountUp';
import { ProductSocialProofSpec } from '../../types';

export type ProductCardProps = {
  product: Product;
  selected: boolean;
  onClick: (product: Product) => void;
  size: 'small' | 'large';
  isValid: boolean;
  quotation?: Quotation | null | undefined;
  socialProof?: ProductSocialProofSpec;
  variant?: 'default' | 'rider';
};

const useStyles = makeStyles<
  Theme,
  {
    selected: boolean;
    discounted: boolean;
    size: 'small' | 'large';
    showSocialProofMessage: boolean;
    isProductWithSocialProof: boolean;
  }
>(theme => ({
  root: {
    padding: theme.spacing(1.5),
  },
  card: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius * 2,
    cursor: 'pointer',
    height: '100%',
    backgroundColor: ({ selected }) => (selected ? BLUSH : 'transparent'),
    border: ({ selected }) =>
      selected ? `solid 1.5px ${BOWTIE_PINK}` : `solid 1.5px ${STONE}`,
  },
  socialProofBadge: {
    position: 'absolute',
    backgroundColor: BOWTIE_BLUE,
    top: 0,
    left: '-5px',
    borderRadius: theme.shape.borderRadius,
    borderBottomLeftRadius: 0,
    color: WHITE,
    padding: theme.spacing(0.1875, 1),
    zIndex: 1,
    '&:after': {
      content: '""',
      position: 'absolute',
      left: 0,
      bottom: '1px',
      transform: 'translateY(100%)',
      borderTop: `6px solid ${BOWTIE_BLUE}`,
      borderLeft: '5px solid transparent',
    },
  },
  activeOpacity: {
    opacity: ({ selected }) => (selected ? 1 : 0.6),
  },
  container: {
    width: 'unset',
    padding: ({ discounted }) =>
      discounted ? theme.spacing(2, 2) : theme.spacing(2.5, 2),
    alignItems: 'stretch',
    height: '100%',
  },
  showSocialProofPadding: {
    padding: ({ size, isProductWithSocialProof }) =>
      size === 'small'
        ? isProductWithSocialProof
          ? theme.spacing(3, 3, 2, 2)
          : theme.spacing(2, 3, 2, 2)
        : theme.spacing(4, 2, 3),
  },
  priceContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: ({ size }) => (size === 'small' ? 'flex-end' : 'flex-start'),
  },
  productNameContainer: {
    paddingRight: ({ size }) => (size === 'small' ? theme.spacing(1) : 0),
    whiteSpace: 'pre-line',
  },
  premium: {
    lineHeight: ({ size }) => (size === 'small' ? 1.25 : 1),
    float: ({ size }) => (size === 'small' ? 'right' : 'none'),
    maxHeight: 40,
  },
  discountStampText: {
    padding: theme.spacing(0, 0.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: BOWTIE_PINK,
    opacity: '90%',
    color: WHITE,
  },
  dollarSign: {
    fontSize: ({ size }) => (size === 'small' ? '0.75rem' : '1rem'),
    verticalAlign: ({ size }) => (size === 'small' ? 8 : 16),
  },
  productDetailContainer: {
    marginBottom: ({ size }) =>
      size === 'small' ? theme.spacing(0) : theme.spacing(2),
    display: ({ size }) => (size === 'small' ? 'flex' : 'block'),
    flexDirection: ({ size }) => (size === 'small' ? 'column' : 'unset'),
    justifyContent: ({ size }) => (size === 'small' ? 'center' : 'unset'),
  },
  premiumBeforeDiscountContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  price: {
    color: ({ discounted }) => (discounted ? BOWTIE_PINK : INK),
    fontWeight: 700,
    fontSize: ({ size }) => (size === 'small' ? '1.5rem' : '2.5rem'),
  },
  riderCheckbox: {
    color: ({ selected }) => (selected ? BOWTIE_PINK : STONE),
    position: 'absolute',
    top: -4,
    right: -4,
    '& svg': {
      height: 32,
      width: 32,
    },
  },
  unit: {
    color: GRAPHITE,
    fontSize: ({ size }) => (size === 'small' ? '0.75rem' : '1rem'),
  },
}));

const ProductCard = ({
  product,
  selected,
  onClick,
  isValid,
  size,
  quotation,
  socialProof,
  variant = 'default',
}: ProductCardProps) => {
  const { locale } = useIntl();

  const handleClick = useCallback(() => {
    onClick(product);
  }, [onClick, product]);

  const classes = useStyles({
    selected,
    discounted: !!quotation?.discount,
    size,
    showSocialProofMessage: !!socialProof,
    isProductWithSocialProof:
      !!socialProof && product.code.includes(socialProof.product_code),
  });

  const countUp = useNumberCountUp(
    quotation && quotation.discount > 0
      ? quotation.pre_levy_premium
      : quotation?.basic_premium,
  );

  const discountOffString = useMemo(() => {
    if (quotation?.discount_config) {
      const { amount_off, percent_off, discount_type } =
        quotation.discount_config;
      const isPercentOff = discount_type === 'percent_off';
      const offString = isPercentOff
        ? `-${Number(percent_off)}%`
        : `-$${Number(amount_off)}`;

      return offString;
    }
    return '';
  }, [quotation?.discount_config]);

  const productName = useMemo(() => {
    if (
      product.code.startsWith('vhis-flexi-premium') &&
      locale.startsWith('zh') &&
      size === 'large'
    ) {
      return product.short_name.replace(' ', '\n');
    }
    return product.short_name;
  }, [locale, product.code, product.short_name, size]);

  return (
    <Box
      position="relative"
      width="100%"
      data-ui-component="Select"
      data-ui-variant={`${startCase(product.code)} Plan`}
    >
      <Grid className={classes.card} onClick={handleClick}>
        {!!socialProof && product.code.includes(socialProof.product_code) && (
          <Typography
            as="span"
            variant="body-s"
            className={classes.socialProofBadge}
          >
            <FormattedMessage
              id="social-proof-message"
              values={{
                percentage: socialProof.comparison_result,
              }}
            />
          </Typography>
        )}
        {variant === 'rider' && (
          <Box className={classes.riderCheckbox}>
            <CheckboxOn />
          </Box>
        )}
        <Grid
          container
          className={clsx(classes.container, classes.activeOpacity, {
            [classes.showSocialProofPadding]: !!socialProof,
          })}
        >
          <Grid
            item
            xs={size === 'small' ? 7 : 12}
            className={classes.productDetailContainer}
          >
            <Hidden xsDown>
              <Typography
                variant="title-s"
                style={{
                  color: selected ? BOWTIE_PINK : GRAPHITE,
                  paddingBottom: '4px',
                }}
              >
                {product.headline}
              </Typography>
            </Hidden>
            <Typography
              variant={size === 'small' ? 'body-l' : 'title-m'}
              fontWeight="bold"
              className={classes.productNameContainer}
            >
              {productName}
            </Typography>
            {product.annual_limit && (
              <Typography
                variant={size === 'small' ? 'body-s' : 'body-m'}
                fontWeight="medium"
                style={{ color: GRAPHITE, lineHeight: '1.25rem' }}
              >
                <FormattedMessage
                  id="coverage.annualLimit"
                  values={{
                    annualLimit: product.annual_limit.toLocaleString(),
                    br: () => (size === 'small' ? ' ' : <br />),
                  }}
                />
              </Typography>
            )}
            {product.lifetime_limit && (
              <Typography
                variant={size === 'small' ? 'body-s' : 'body-m'}
                fontWeight="medium"
                style={{ color: GRAPHITE, lineHeight: '1.25rem' }}
              >
                <FormattedMessage
                  id="coverage.lifetimeLimit"
                  values={{
                    lifetimeLimit: product.lifetime_limit.toLocaleString(),
                    br: () => (size === 'small' ? ' ' : <br />),
                  }}
                />
              </Typography>
            )}
          </Grid>
          {isValid && (
            <Grid
              item
              xs={size === 'small' ? 5 : 12}
              className={classes.priceContainer}
            >
              {!!quotation && quotation.discount > 0 && (
                <Box
                  display="inline-flex"
                  alignItems="center"
                  gridGap={4}
                  maxHeight={24}
                >
                  <Typography
                    as="span"
                    variant={size === 'small' ? 'body-s' : 'body-l'}
                    fontWeight="medium"
                    style={{ color: GRAPHITE, textDecoration: 'line-through' }}
                  >{`$${Math.round(quotation.basic_premium)}`}</Typography>
                  <Typography
                    variant="body-s"
                    fontWeight="medium"
                    className={classes.discountStampText}
                  >
                    {discountOffString}
                  </Typography>
                </Box>
              )}
              <Typography
                variant="body-l"
                fontWeight="medium"
                className={classes.premium}
              >
                <span className={classes.dollarSign}>$</span>
                <span className={classes.price}>{countUp}</span>
                <span className={classes.unit}>
                  /<FormattedMessage id="month" />
                </span>
              </Typography>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductCard;
